import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Select from 'react-select';

import { MachineActions } from "redux/actions/settings/MachineActions";
import { MachineHistoryActions } from "redux/actions/machines/MachineHistoryActions";
import { FuelTypeActions } from "redux/actions/settings/FuelTypeActions";
import { BarChart, BarChartEntry, format } from "app/components/common/BarChart";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import Translator from "app/components/common/Translator";
import { Translate } from "infrastructure/mapping/Translate";

import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { FuelType } from "models/FuelType";
import { Machine } from "models/Machine";
import { MachineHistory } from "models/MachineHistory";
import { Translations } from "models/Translations";
import { Unit } from "models/Unit";


interface Props {
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fetchFuelTypes: typeof FuelTypeActions.fetchFuelTypes;
    loginUser: LoginUser;
    machines: Machine[];
    machineHistories: MachineHistory[];
    fuelTypes: FuelType[];
    translations: Translations;
}

interface State {
    selectedOption: SelectedOption | null;
}

type SelectedOption = {
    label: string,
    value: number
}

// One fuel stop of the selected machine. The fuel refilled at a stop is what was
// consumed since the previous stop of the same fuel type, so the consumption is
// that quantity per 100 units of service life in between. The first stop of a fuel
// type has no previous stop and therefore no consumption.
export interface FuelStatistic {
    fuelType: FuelType;
    quantity: number;
    serviceLife: number;
    previousServiceLife?: number;
    consumption?: number;
}

export function calculateStatistics(machineHistories: MachineHistory[], machineId: number): FuelStatistic[] {
    const sorted = machineHistories
        .filter(mh => mh.machine.id === machineId && !mh.archivedDate && mh.fuelStop && !mh.fuelStop.archivedDate && mh.fuelStop.fuelType)
        .sort((a, b) => a.serviceLife - b.serviceLife);

    let previous = new Map<number, MachineHistory>();
    return sorted.map(mh => {
        const fuelType = mh.fuelStop!.fuelType;
        const prev = previous.get(fuelType.id);
        previous.set(fuelType.id, mh);
        const interval = prev ? mh.serviceLife - prev.serviceLife : 0;
        return {
            fuelType: fuelType,
            quantity: mh.fuelStop!.quantity,
            serviceLife: mh.serviceLife,
            previousServiceLife: prev?.serviceLife,
            consumption: interval > 0 ? mh.fuelStop!.quantity / interval * 100 : undefined,
        };
    });
}

function median(values: number[]) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

class MachineStatistics extends React.Component<Props, State> {
    public readonly state: State = {
        selectedOption: null
    }

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
            if (!this.props.fuelTypes) {
                this.props.fetchFuelTypes(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machines && this.props.machineHistories
    }

    getOptions(): SelectedOption[] {
        return this.props.machines
            .filter(m => !m.archivedDate)
            .map(m => ({ label: m.name, value: m.id }));
    }

    getSelectedOption(options: SelectedOption[]) {
        const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
        return this.state.selectedOption ?? options.find(o => o.value === assigned?.id) ?? options[0] ?? null;
    }

    getFuelTypes(statistics: FuelStatistic[]): FuelType[] {
        let fuelTypes: FuelType[] = [];
        statistics.forEach(s => {
            if (!fuelTypes.find(ft => ft.id === s.fuelType.id))
                // the fuel type list carries the unit, the one on the history entry might not
                fuelTypes.push(this.props.fuelTypes?.find(ft => ft.id === s.fuelType.id) ?? s.fuelType);
        });
        return fuelTypes.sort((a, b) => a.name.localeCompare(b.name));
    }

    getServiceLifeUnit(machineId: number): Unit | null | undefined {
        return this.props.machines.find(m => m.id === machineId)?.machineType?.serviceLifeUnit;
    }

    // appends the unit shorthand to a label or value, if there is a unit
    withUnit(text: string, unit?: Unit | null) {
        return unit && unit.short ? text + " " + unit.short : text;
    }

    // "Fuel per 100 h" if the service life unit is known, the generic label otherwise
    consumptionLabel(serviceLifeUnit?: Unit | null) {
        if (serviceLifeUnit?.short)
            return Translate.text(this.props.translations, "Fuel per 100 {unit}").replace("{unit}", serviceLifeUnit.short);
        return Translate.text(this.props.translations, "Fuel per 100 Service Life");
    }

    setChartData(statistics: FuelStatistic[], fuelType: FuelType, serviceLifeUnit?: Unit | null): BarChartEntry[] {
        const t = (text: string) => Translate.text(this.props.translations, text);
        return statistics
            .filter(s => s.fuelType.id === fuelType.id && s.consumption !== undefined)
            .map(s => ({
                from: s.previousServiceLife!,
                to: s.serviceLife,
                value: s.consumption!,
                tooltip: [
                    t("Service Life") + ": " + this.withUnit(format(s.serviceLife - s.previousServiceLife!), serviceLifeUnit),
                    t("Fuel") + ": " + this.withUnit(s.quantity + "", fuelType.unit) + " " + fuelType.name,
                    this.consumptionLabel(serviceLifeUnit) + ": " + this.withUnit(format(s.consumption!), fuelType.unit),
                ]
            }));
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Machine Statistics" />
                    <Translator text="Loading" />
                </div>
            )
        }
        const options = this.getOptions();
        const selectedOption = this.getSelectedOption(options);
        const statistics = selectedOption ? calculateStatistics(this.props.machineHistories, selectedOption.value) : [];
        const fuelTypes = this.getFuelTypes(statistics);
        const serviceLifeUnit = selectedOption ? this.getServiceLifeUnit(selectedOption.value) : undefined;
        return (
            <div className="c-page__content">
                <PageContentHeader text="Machine Statistics" />
                <div className="c-card">
                    <Select
                        value={selectedOption}
                        onChange={selectableOption => {
                            if (selectableOption)
                                this.setState({ selectedOption: selectableOption });
                        }}
                        options={options}
                        placeholder={Translate.text(this.props.translations, "Machine")}
                        className={"c-form__select"}
                        classNamePrefix="c-select"
                        // render the menu outside the card, which would clip it
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 10 }) }}
                    />
                </div>
                {fuelTypes.length === 0 &&
                    <div className="c-card">
                        <div className="c-table__empty">
                            <Translator text="No data yet" />
                        </div>
                    </div>
                }
                {fuelTypes.map(fuelType => {
                    const data = this.setChartData(statistics, fuelType, serviceLifeUnit);
                    return (
                        <div className="c-card" key={fuelType.id}>
                            <h3>{fuelType.name}</h3>
                            <BarChart
                                data={data}
                                xLabel={Translate.text(this.props.translations, "Service Life") + (serviceLifeUnit?.short ? " (" + serviceLifeUnit.short + ")" : "")}
                                yLabel={this.consumptionLabel(serviceLifeUnit) + (fuelType.unit?.short ? " (" + fuelType.unit.short + ")" : "")}
                                referenceLine={data.length > 0 ? { value: median(data.map(d => d.value)), label: Translate.text(this.props.translations, "Median") } : undefined}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineHistories: state.machineHistories,
        fuelTypes: state.fuelTypes,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        fetchFuelTypes: bindActionCreators(FuelTypeActions.fetchFuelTypes, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MachineStatistics);
